import { Css, GridDataRow, simpleHeader } from "@homebound/beam";
import { addDays, addWeeks, isAfter, isBefore, isSameDay, startOfToday, startOfWeek, subDays } from "date-fns";
import { useRouteMatch } from "react-router";
import { TaskDetailsPage_ProjectItemFragment } from "src/fragments/circularFragments.generated";
import {
  BillSource,
  PlanTask,
  SaveBillInput,
  SchedulingExclusionDate,
  TaskStatus,
  TradePartnerTaskStatus,
} from "src/generated/graphqlTypes";
import { PlanTaskBillModal_ProtoBillFragment } from "src/routes/projects/dynamic-schedules/components/bill-modal/PlanTaskBillModal.generated";
import {
  ScheduleDraftMode_PlanMilestoneFragment,
  ScheduleDraftMode_PlanTaskFragment,
} from "src/routes/projects/dynamic-schedules/draft-mode/ScheduleDraftMode.generated";
import { dynamicSchedulesPath } from "src/routes/routesDef";
import { calcPercent, findIndexFrom, isDefined } from "src/utils";
import { DateOnly, formatShort } from "src/utils/dates";

// Shared type for task detail sections that display project items
export type ProjectItemsSection = {
  projectItems: TaskDetailsPage_ProjectItemFragment[];
  // Don't render border and extra padding when reusing section in side pane
  inSidePane?: boolean;
};

export function ScheduleContainer({ children }: { children: React.ReactNode }) {
  return <div css={Css.h100.w100.pr3.ma.$}>{children}</div>;
}

export enum DynamicScheduleView {
  Calendar = "Calendar",
  Gantt = "Gantt",
  List = "List",
  Lookahead = "Lookahead",
  Milestone = "Milestone",
}

export function useScheduleRouteMatch() {
  const isGanttView = !!useRouteMatch(dynamicSchedulesPath.draftGantt);
  const isCalendarView = !!useRouteMatch(dynamicSchedulesPath.draftCalendar);
  const isListView = !!useRouteMatch(dynamicSchedulesPath.draftMode)?.isExact;
  const isMilestoneView = !!useRouteMatch(dynamicSchedulesPath.draftMilestone);
  const isLookaheadView = !!useRouteMatch(dynamicSchedulesPath.draftLookahead);

  if (isGanttView) return DynamicScheduleView.Gantt;
  if (isCalendarView) return DynamicScheduleView.Calendar;
  if (isMilestoneView) return DynamicScheduleView.Milestone;
  if (isListView) return DynamicScheduleView.List;
  if (isLookaheadView) return DynamicScheduleView.Lookahead;

  throw new Error("Unknown schedule view");
}

/**
 * Inserts lookahead divider rows into the task rows when a dynamic schedule list displays a lookahead view.
 */
export function insertLookaheadDividerRows(taskRows: DynamicSchedulesRow[]): DynamicSchedulesRow[] {
  const mondayOfThisWeek = startOfWeek(startOfToday(), { weekStartsOn: 1 });

  const lookaheadRows: GridDataRow<LookaheadRow>[] = [
    {
      kind: "lookahead" as const,
      id: "2 Weeks Divider",
      data: { name: "2 Weeks", startDate: mondayOfThisWeek, endDate: subDays(addWeeks(mondayOfThisWeek, 2), 1) },
    },
    {
      kind: "lookahead" as const,
      id: "3 Weeks Divider",
      data: {
        name: "3 Weeks",
        startDate: addWeeks(mondayOfThisWeek, 2),
        endDate: subDays(addWeeks(mondayOfThisWeek, 3), 1),
      },
    },
    {
      kind: "lookahead" as const,
      id: "4 Weeks Divider",
      data: {
        name: "4 Weeks",
        startDate: addWeeks(mondayOfThisWeek, 3),
        endDate: subDays(addWeeks(mondayOfThisWeek, 4), 1),
      },
    },
    {
      kind: "lookahead" as const,
      id: "6 Weeks Divider",
      data: {
        name: "6 Weeks",
        startDate: addWeeks(mondayOfThisWeek, 4),
        endDate: subDays(addWeeks(mondayOfThisWeek, 6), 1),
      },
    },
  ];

  lookaheadRows.forEach((lar) => {
    // Find the first task that starts between the lookahead rows start and end date and insert lar right before it.
    const firstTaskIndex = findIndexFrom(taskRows, (row) => {
      const { kind, data } = row;
      let starter: Date;
      switch (kind) {
        case "task":
        case "lookahead":
          starter = data.startDate;
          break;
        // Milestones can technically not have an estStartDate
        case "milestone":
          starter = data?.estStartDate ?? subDays(mondayOfThisWeek, 1);
          break;
        case "header":
        default:
          starter = subDays(mondayOfThisWeek, 1);
          break;
      }

      return (
        (isSameDay(starter, lar.data.startDate) || isAfter(starter, lar.data.startDate)) &&
        isBefore(starter, lar.data.endDate)
      );
    });
    if (firstTaskIndex !== -1) {
      taskRows.splice(firstTaskIndex, 0, lar);
    }
  });

  return taskRows;
}

/* Maps our DayOfWeek enum to numeric day */
export enum DayOfWeekDayPicker {
  SUNDAY = 0,
  MONDAY = 1,
  TUESDAY = 2,
  WEDNESDAY = 3,
  THURSDAY = 4,
  FRIDAY = 5,
  SATURDAY = 6,
}

/** Maps PlanTask.customWorkableDays and PlanSchedule.scheduleExcludedDates to the correct input for `date-fns` business day helpers */
export function getBusinessDaysOptions(
  customWorkableDays: PlanTask["customWorkableDays"],
  scheduleExcludedDates?: Pick<SchedulingExclusionDate, "date">[],
) {
  return {
    businessDays: customWorkableDays?.map((d) => DayOfWeekDayPicker[d]),
    exceptions: scheduleExcludedDates?.keyBy(
      (sed) => formatShort(sed.date),
      () => false,
    ),
  };
}

type IsDisableDayInput = {
  date: DateOnly | Date;
  customWorkableDays: PlanTask["customWorkableDays"];
  scheduleExcludedDates?: DateOnly[];
};

export function isDisabledDay({ date, customWorkableDays, scheduleExcludedDates }: IsDisableDayInput) {
  const totalWorkingDays = customWorkableDays?.map((d) => DayOfWeekDayPicker[d]);

  const isWorkingDayForTask = totalWorkingDays.includes(date.getDay());
  const isHoliday = scheduleExcludedDates?.some((d) => isSameDay(d, date));

  return !isWorkingDayForTask || isHoliday;
}

/** Shared util to check if a task is in the "2 week window" and has not confirmed the trade */
export function taskRequiresTradeConfirmation(
  taskStatus: TaskStatus,
  taskStartDate: DateOnly,
  tradeStatus: TradePartnerTaskStatus,
) {
  const confirmedStatuses = [TradePartnerTaskStatus.Confirmed, TradePartnerTaskStatus.CompletedJob];
  return (
    taskStatus !== TaskStatus.Complete &&
    isBefore(taskStartDate.date, addDays(new Date(), 14)) &&
    !confirmedStatuses.includes(tradeStatus)
  );
}
export type LookaheadRow = { kind: "lookahead"; id: string; data: { name: string; startDate: Date; endDate: Date } };
export type DynamicSchedulesRow =
  | DynamicSchedulesHeaderRow
  | DynamicSchedulesGroupRow
  | LookaheadRow
  | DynamicSchedulesTaskRow
  | DynamicSchedulesMilestoneRow;

type DynamicSchedulesHeaderRow = { kind: "header"; id: string; data: undefined };
type DynamicSchedulesGroupRow = { kind: "group"; id: string; data: { name: string; progress?: number } };
export type DynamicSchedulesTaskRow = { kind: "task"; id: string; data: ScheduleDraftMode_PlanTaskFragment };

export type DynamicSchedulesMilestoneRow = {
  kind: "milestone";
  id: string;
  data: ScheduleDraftMode_PlanMilestoneFragment;
};

export function createTaskAndMilestoneRows(
  planTasks: ScheduleDraftMode_PlanTaskFragment[],
  planMilestones: ScheduleDraftMode_PlanMilestoneFragment[],
  isLookaheadView: boolean,
): GridDataRow<DynamicSchedulesRow>[] {
  const taskRows: GridDataRow<DynamicSchedulesTaskRow>[] = planTasks.map((task) => ({
    kind: "task" as const,
    id: task.id,
    data: task,
  }));

  const planMilestoneRows: GridDataRow<DynamicSchedulesMilestoneRow>[] = (
    isLookaheadView ? filterMilestonesForLookahead(planMilestones) : planMilestones
  ).map((pm) => ({ id: pm.id, kind: "milestone" as const, data: pm }));

  const sortedTaskAndMilestones = [...planMilestoneRows, ...taskRows].sort(sortTaskAndMilestoneRows);

  return [
    simpleHeader,
    ...(isLookaheadView ? insertLookaheadDividerRows(sortedTaskAndMilestones) : sortedTaskAndMilestones),
  ];
}

/** Assign each milestone to the group (stage/cost code) of its earliest task. */
function buildMilestoneGroupMap(
  planTasks: ScheduleDraftMode_PlanTaskFragment[],
  planMilestones: ScheduleDraftMode_PlanMilestoneFragment[],
  getGroupKey: (task: ScheduleDraftMode_PlanTaskFragment) => string,
): Record<string, ScheduleDraftMode_PlanMilestoneFragment[]> {
  if (planMilestones.isEmpty) return {};
  const milestoneIds = planMilestones.map((m) => m.id).unique();
  // Find which group each milestone belongs to by picking the earliest task that references it
  const milestoneToGroupKey = new Map<string, { groupKey: string; startDate: Date }>();
  for (const task of planTasks) {
    for (const milestone of task.allowsPlanMilestones) {
      if (!milestoneIds.includes(milestone.id)) continue;
      const existing = milestoneToGroupKey.get(milestone.id);
      if (!existing || task.startDate < existing.startDate) {
        milestoneToGroupKey.set(milestone.id, { groupKey: getGroupKey(task), startDate: task.startDate });
      }
    }
  }
  // Return milestones grouped by key
  return planMilestones
    .filter((m) => milestoneToGroupKey.has(m.id))
    .groupBy((m) => milestoneToGroupKey.get(m.id)!.groupKey);
}

export function createStageRows(
  planTasks: ScheduleDraftMode_PlanTaskFragment[],
  allTasks: ScheduleDraftMode_PlanTaskFragment[],
  planMilestones: ScheduleDraftMode_PlanMilestoneFragment[] = [],
): GridDataRow<DynamicSchedulesRow>[] {
  const stageGroups = planTasks.groupByObject((task) => task.stage);
  const noStageId = "no-stage";
  // we need to group all tasks by stage to calculate the progress (regardless of the task filters for "show completed")
  const allTasksStageGroups = allTasks?.groupBy((task) => task.stage?.id ?? noStageId);
  const milestoneStageMap = buildMilestoneGroupMap(planTasks, planMilestones, (task) => task.stage?.id ?? noStageId);
  const sortedStageGroups = stageGroups.sort(([aStage, aTasks], [bStage, bTasks]) => {
    const aHasOrder = isDefined(aStage?.orderForProject);
    const bHasOrder = isDefined(bStage?.orderForProject);

    // If both have orderForProject, sort by it
    if (aHasOrder && bHasOrder) {
      return aStage!.orderForProject! - bStage!.orderForProject!;
    }

    // Stages with orderForProject come before stages without
    if (aHasOrder !== bHasOrder) {
      return aHasOrder ? -1 : 1;
    }

    // Fall back to earliest start date of the tasks in the stage
    const aEarliestDate = aTasks.min((t) => t.startDate)?.getTime() ?? 0;
    const bEarliestDate = bTasks.min((t) => t.startDate)?.getTime() ?? 0;
    return aEarliestDate - bEarliestDate;
  });

  return [
    simpleHeader,
    ...sortedStageGroups.flatMap(([stage, tasks]) => {
      const stageId = stage?.id ?? noStageId;
      const stageMilestones = (milestoneStageMap[stageId] ?? []).map((pm) => ({
        kind: "milestone" as const,
        id: pm.id,
        data: pm,
      }));
      const taskRows = tasks.map((task) => ({ kind: "task" as const, id: task.id, data: task }));
      const children = [...taskRows, ...stageMilestones].sort(sortTaskAndMilestoneRows);
      return [
        {
          kind: "group" as const,
          id: stageId,
          data: {
            name: stage?.name ?? "No Stage",
            progress: calculateStageGroupProgress(allTasksStageGroups[stageId] ?? []),
          },
          children,
          // collapse the stage if every task has been completed in that stage
          initCollapsed: tasks.every((t) => t.status.code === TaskStatus.Complete),
        },
      ];
    }),
  ];
}

export function createCostCodeRows(
  planTasks: ScheduleDraftMode_PlanTaskFragment[],
  planMilestones: ScheduleDraftMode_PlanMilestoneFragment[] = [],
): GridDataRow<DynamicSchedulesRow>[] {
  const costCodeGroups = planTasks.groupByObject((task) => task.globalPlanTask.budgetItem.costCode);
  const milestoneCostCodeMap = buildMilestoneGroupMap(
    planTasks,
    planMilestones,
    (task) => task.globalPlanTask.budgetItem.costCode.id,
  );
  return [
    simpleHeader,
    ...costCodeGroups
      .sortBy(([_, tasks]) => {
        const earliestDate = tasks.min((t) => t.startDate);
        return earliestDate?.getTime() ?? 0;
      })
      .flatMap(([costCode, tasks]) => {
        const costCodeMilestones = (milestoneCostCodeMap[costCode.id] ?? []).map((pm) => ({
          kind: "milestone" as const,
          id: pm.id,
          data: pm,
        }));
        const taskRows = tasks.map((task) => ({ kind: "task" as const, id: task.id, data: task }));
        const children = [...taskRows, ...costCodeMilestones].sort(sortTaskAndMilestoneRows);
        return [{ kind: "group" as const, id: costCode.id, data: { name: costCode.name }, children }];
      }),
  ];
}

export function sortTaskAndMilestoneRows(
  a: GridDataRow<DynamicSchedulesTaskRow | DynamicSchedulesMilestoneRow>,
  b: GridDataRow<DynamicSchedulesTaskRow | DynamicSchedulesMilestoneRow>,
) {
  const getCompareValues = (row: GridDataRow<DynamicSchedulesTaskRow | DynamicSchedulesMilestoneRow>) => ({
    completed:
      row.kind === "task"
        ? row.data.status.code === TaskStatus.Complete
        : (row.data as ScheduleDraftMode_PlanMilestoneFragment).progress === 100,
    startDate:
      row.kind === "task"
        ? row.data.startDate
        : ((row.data as ScheduleDraftMode_PlanMilestoneFragment)?.estStartDate ?? new Date()),
    name: row.data.name.toLowerCase(),
  });

  const aCompare = getCompareValues(a);
  const bCompare = getCompareValues(b);

  // Completed always first
  if (aCompare.completed !== bCompare.completed) return aCompare.completed ? -1 : 1;
  // Then by date start
  if (aCompare.startDate !== bCompare.startDate) return aCompare.startDate < bCompare.startDate ? -1 : 1;

  // Finally by name
  return aCompare.name.localeCompare(bCompare.name);
}

// FIXME: This logic isn't ideal but while we're inbetween combining draft and standard views, and still
//  refining what is "normal" for these tables we will carry this debt
export function filterMilestonesForLookahead<T extends { estStartDate?: DateOnly | null | undefined }>(
  planMilestones: T[],
) {
  const mondayOfThisWeek = startOfWeek(startOfToday(), { weekStartsOn: 1 });

  return planMilestones.filter(
    ({ estStartDate }) =>
      !!estStartDate && (isSameDay(estStartDate, mondayOfThisWeek) || isAfter(estStartDate, mondayOfThisWeek)),
  );
}

function calculateStageGroupProgress(tasks: ScheduleDraftMode_PlanTaskFragment[]): number {
  if (tasks.isEmpty) return 0; // Return 0 if no stage groups are provided

  const completedTasksCount = tasks.filter(({ status }) => status.code === TaskStatus.Complete).length;
  return Math.round(calcPercent(completedTasksCount, tasks.length));
}

// ------------ Plan Task billing commons ------------

export const ungroupedTradeLineId = "no-group";

export type DrawRow = {
  kind: "draw";
  data: { drawBasisPoints: number; drawDescription?: string | null; lineItemTotal: number };
};
export type TradeLineRow = { kind: "tradeLine"; data: { displayName: string; lineItemTotal: number } };
export type OwnerRow = {
  kind: "lineItemOwner";
  data: { tradePartnerName: string | undefined; accountingNumber: number; ownerType: string };
};
export type PlanTaskBillRow = OwnerRow | DrawRow | TradeLineRow;

export function groupPlanTaskProtoBills(
  protoBills: PlanTaskBillModal_ProtoBillFragment[],
  ungroupedTradeLineIdentifier: string = ungroupedTradeLineId,
) {
  // First group the protoBills by Commitment / CommitmentChangeOrder (owner of the line item)
  const lineItemsByOwner = protoBills
    .flatMap((protoBill) => protoBill.lineItems)
    .groupByObject((li) => li.commitmentLineItem.owner);

  return lineItemsByOwner.map(([lineItemOwner, lineItems], idx) => {
    // In the unlikely event that line items are not part of a "draw" and also do not have a "tradeLineItem",
    // then bucket them together into a "Misc" section (using this object identity for the `groupByObject` below).
    const ungroupedTradeLineBucket = {
      id: `${ungroupedTradeLineIdentifier}-${idx}`,
      options: [{ displayName: "Misc" }],
    } as const;

    // Separate out line items associated a "commitmentDraw" then group the non-draw items by trade line
    const [drawLineItems, nonDrawItems] = lineItems.partition((li) => isDefined(li.commitmentDraw));
    const draws = drawLineItems.groupByObject((li) => li.commitmentDraw!);
    const tradeLines = nonDrawItems.groupByObject(
      (li) => li.commitmentLineItem.primaryBidContractLineItem?.tradeLineItem ?? ungroupedTradeLineBucket,
    );
    return { lineItemOwner, draws, tradeLines };
  });
}

export function getPlanTaskLineItemRows(
  lineItemsByOwner: ReturnType<typeof groupPlanTaskProtoBills>,
): GridDataRow<PlanTaskBillRow>[] {
  return lineItemsByOwner.map(({ lineItemOwner, draws, tradeLines }) => {
    const drawRows = draws.map(([commitmentDraw, drawLineItems]) => ({
      kind: "draw" as const,
      id: commitmentDraw.id,
      initSelected: true,
      data: {
        drawBasisPoints: commitmentDraw.amountInBasisPoints,
        drawDescription: commitmentDraw.description,
        lineItemTotal: drawLineItems.sum((li) => li.amountInCents),
      },
    }));

    const tradeLineRows = tradeLines.map(([tradeLine, lineItems]) => ({
      kind: "tradeLine" as const,
      id: tradeLine.id,
      initSelected: true,
      data: {
        displayName: tradeLine.options.nonEmpty ? tradeLine.options.map((o) => o.displayName).join(", ") : "Misc",
        lineItemTotal: lineItems.sum((li) => li.amountInCents),
      },
    }));

    return {
      kind: "lineItemOwner",
      id: lineItemOwner.id,
      data: {
        tradePartnerName: lineItemOwner.tradePartner?.name,
        accountingNumber: lineItemOwner.accountingNumber,
        ownerType: lineItemOwner.__typename === "Commitment" ? "PO" : "CO",
      },
      children: [...drawRows, ...tradeLineRows],
    };
  });
}

export function mapPlanTaskToSaveBillInput(
  taskId: string,
  protoBills: PlanTaskBillModal_ProtoBillFragment[],
  deselectedClis: string[],
  tradePartnerId: string | undefined,
  internalNote: string | undefined,
): SaveBillInput[] {
  return protoBills.flatMap((protoBill) => {
    const { tradePartner } = protoBill;
    // there is an oversight currently in protoBills where bills not from this trade are returned. So we should filter
    // to just bills from this trade until that is fixed.
    const existingBills = protoBill.existingBills.filter((b) => b.tradePartner.id === tradePartner.id);
    const projectStageId = protoBill.lineItems.first?.commitmentLineItem.projectItem?.projectStage.id;
    // split bills into deferred and immediate types
    return [true, false]
      .map((isDeferred) => {
        const lineItemsForType = tradePartnerId
          ? // if we are in "pay trade" mode, we only create immediate line items
            isDeferred
            ? []
            : protoBill.lineItems
          : protoBill.lineItems.filter((li) => deselectedClis.includes(li.commitmentLineItem.id) === isDeferred);

        if (lineItemsForType.isEmpty) return undefined;

        const newLineItems = lineItemsForType.map((li) => ({
          taskId,
          commitmentDrawId: li.commitmentDraw?.id,
          commitmentLineItemId: li.commitmentLineItem.id,
          amountInCents: li.amountInCents,
        }));

        const existingBill = existingBills.find((b) => b.isDeferred === isDeferred);

        if (existingBill) {
          const existingLineItems = existingBill.lineItems
            // Just in case, make sure we're only passing one line item per commitmentLineItem
            .filter((li) => !newLineItems.some((input) => input.commitmentLineItemId === li.commitmentLineItem?.id))
            .map((li) => ({ id: li.id }));
          return {
            id: existingBill.id,
            lineItems: [...existingLineItems, ...newLineItems],
            // preserve previous deferred notes & append notes for new items added
            ...(isDeferred ? { internalNote: [existingBill.internalNote, internalNote].compact().join(",\n") } : {}),
          };
        } else {
          return {
            source: BillSource.ClickToPay,
            isDeferred,
            tradePartnerId: tradePartner?.id,
            projectStageId,
            lineItems: newLineItems,
            ...(isDeferred ? { internalNote } : {}),
          };
        }
      })
      .compact();
  });
}
